import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Shield, RotateCw, Eye, ChevronDown, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { API } from '../../config';
import { exportCSV, exportPDF } from '../../utils/exportUtils';

const severityStyles = {
  high: 'bg-red-500/10 border-red-500/30',
  medium: 'bg-amber-500/10 border-amber-500/30',
  low: 'bg-blue-500/10 border-blue-500/30',
};

const statusStyles = {
  new: { label: 'Nouveau', bg: 'bg-red-500/20 text-red-400' },
  in_progress: { label: 'En cours', bg: 'bg-amber-500/20 text-amber-400' },
  resolved: { label: 'Résolu', bg: 'bg-emerald-500/20 text-emerald-400' },
  ignored: { label: 'Ignoré', bg: 'bg-secondary text-muted-foreground' },
};

const typeLabels = {
  SHARED_FINGERPRINT: 'Empreinte partagée',
  RAPID_ROTATION: 'Rotation rapide',
  OVER_LIMIT: 'Dépassement limite',
};

const typeIcons = {
  SHARED_FINGERPRINT: Shield,
  RAPID_ROTATION: RotateCw,
  OVER_LIMIT: AlertTriangle,
};

export function AdminFraudAlerts({ token, onNavigateToUser }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active, all, resolved

  const loadAlerts = () => {
    setLoading(true);
    axios.get(`${API}/admin/fraud-alerts`, { headers })
      .then(r => setAlerts(r.data.alerts))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAlerts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (alertId, newStatus, notes = '') => {
    try {
      await axios.put(`${API}/admin/fraud-alerts/${alertId}`, { status: newStatus, notes }, { headers });
      toast.success(`Alerte marquée : ${statusStyles[newStatus]?.label || newStatus}`);
      loadAlerts();
    } catch (e) { toast.error('Erreur'); }
  };

  const filtered = alerts.filter(a => {
    if (filter === 'active') return a.status === 'new' || a.status === 'in_progress';
    if (filter === 'resolved') return a.status === 'resolved' || a.status === 'ignored';
    return true;
  });

  const activeCount = alerts.filter(a => a.status === 'new' || a.status === 'in_progress').length;

  if (loading) return <p className="text-muted-foreground text-sm">Analyse en cours...</p>;

  return (
    <div className="space-y-4" data-testid="admin-fraud-alerts">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {['active', 'all', 'resolved'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${filter === f ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}>
              {f === 'active' ? `Actives (${activeCount})` : f === 'all' ? 'Toutes' : 'Résolues'}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={loadAlerts} className="text-muted-foreground h-7" data-testid="refresh-alerts-btn">
          <RotateCw className="w-3.5 h-3.5 mr-1" /> Actualiser
        </Button>
        <Button variant="ghost" size="sm" onClick={() => exportCSV(filtered.map(a => ({
          Type: typeLabels[a.type] || a.type, Sévérité: a.severity, Statut: statusStyles[a.status]?.label || a.status,
          Message: a.message, Email: a.user_email || '', Notes: a.notes || '',
          Détecté: a.detected_at?.slice(0, 10),
        })), 'alertes_fraude')} className="text-muted-foreground h-7" data-testid="export-alerts-csv">
          <Download className="w-3.5 h-3.5 mr-1" /> CSV
        </Button>
        <Button variant="ghost" size="sm" onClick={() => exportPDF(filtered.map(a => ({
          Type: typeLabels[a.type] || a.type, Sévérité: a.severity, Statut: statusStyles[a.status]?.label || a.status,
          Message: a.message, Email: a.user_email || '',
          Détecté: a.detected_at?.slice(0, 10),
        })), 'alertes_fraude', 'Alertes Fraude')} className="text-muted-foreground h-7" data-testid="export-alerts-pdf">
          <Download className="w-3.5 h-3.5 mr-1" /> PDF
        </Button>
      </div>

      {filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm text-center py-8">
          {filter === 'active' ? 'Aucune alerte active' : 'Aucune alerte'}
        </p>
      ) : filtered.map((alert) => (
        <AlertCard key={alert.id} alert={alert} onUpdateStatus={updateStatus} headers={headers} />
      ))}
    </div>
  );
}

function AlertCard({ alert, onUpdateStatus, headers }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(alert.notes || '');
  const Icon = typeIcons[alert.type] || AlertTriangle;
  const ss = statusStyles[alert.status] || statusStyles.new;
  const sevStyle = severityStyles[alert.severity] || severityStyles.low;

  const userIds = alert.user_ids || (alert.user_id ? [alert.user_id] : []);
  const userEmail = alert.user_email || (alert.users?.[0]?.email) || '';

  return (
    <div className={`rounded-xl border ${sevStyle} overflow-hidden`} data-testid={`fraud-alert-${alert.id}`}>
      <div className="p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start gap-3">
          <Icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-inherit" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-sm font-medium text-foreground">{typeLabels[alert.type] || alert.type}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${alert.severity === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                {alert.severity === 'high' ? 'CRITIQUE' : 'MOYEN'}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${ss.bg}`}>{ss.label}</span>
            </div>
            <p className="text-xs text-muted-foreground">{alert.message}</p>
            {userEmail && <p className="text-xs text-foreground/60 mt-1">{userEmail}</p>}
          </div>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/30 pt-3">
          {/* Détails */}
          {alert.type === 'SHARED_FINGERPRINT' && alert.users && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Comptes concernés :</p>
              {alert.users.map((u, i) => (
                <div key={i} className="flex items-center gap-2 text-xs mb-1">
                  <span className="text-foreground">{u.email}</span>
                  <span className="text-muted-foreground">({u.name || 'N/A'})</span>
                  <a href={`/admin?user=${u.id}`}
                    className="text-blue-400 hover:text-blue-300 flex items-center gap-0.5"
                    onClick={(e) => { e.stopPropagation(); }}
                    data-testid={`alert-view-user-${u.id}`}>
                    <Eye className="w-3 h-3" /> Voir
                  </a>
                </div>
              ))}
              <p className="text-[10px] text-muted-foreground mt-1">Fingerprint : {alert.device_fingerprint?.slice(0, 40)}...</p>
            </div>
          )}

          {alert.type === 'RAPID_ROTATION' && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">{alert.user_email} - {alert.event_count} événements en 7j</span>
              {alert.user_id && (
                <a href={`/admin?user=${alert.user_id}`} className="text-blue-400 hover:text-blue-300 flex items-center gap-0.5" data-testid={`alert-view-user-${alert.user_id}`}>
                  <Eye className="w-3 h-3" /> Voir l'utilisateur
                </a>
              )}
            </div>
          )}

          {alert.type === 'OVER_LIMIT' && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">{alert.user_email} - {alert.active_devices} actifs / {alert.device_limit} max</span>
              {alert.user_id && (
                <a href={`/admin?user=${alert.user_id}`} className="text-blue-400 hover:text-blue-300 flex items-center gap-0.5" data-testid={`alert-view-user-${alert.user_id}`}>
                  <Eye className="w-3 h-3" /> Voir l'utilisateur
                </a>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-[10px] text-muted-foreground mb-1 block">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Ajouter des notes..."
              className="w-full px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-foreground resize-none" />
          </div>

          {/* Actions de statut */}
          <div className="flex items-center gap-2 flex-wrap">
            {alert.status !== 'in_progress' && (
              <Button size="sm" variant="outline" className="h-7 text-xs text-amber-400 border-amber-400/30 hover:bg-amber-500/10"
                onClick={() => onUpdateStatus(alert.id, 'in_progress', notes)} data-testid={`alert-inprogress-${alert.id}`}>
                En cours
              </Button>
            )}
            {alert.status !== 'resolved' && (
              <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-400 border-emerald-400/30 hover:bg-emerald-500/10"
                onClick={() => onUpdateStatus(alert.id, 'resolved', notes)} data-testid={`alert-resolve-${alert.id}`}>
                Résolu
              </Button>
            )}
            {alert.status !== 'ignored' && (
              <Button size="sm" variant="outline" className="h-7 text-xs text-muted-foreground border-border hover:bg-secondary"
                onClick={() => onUpdateStatus(alert.id, 'ignored', notes)} data-testid={`alert-ignore-${alert.id}`}>
                Ignorer
              </Button>
            )}
            {(alert.status === 'resolved' || alert.status === 'ignored') && (
              <Button size="sm" variant="outline" className="h-7 text-xs text-red-400 border-red-400/30 hover:bg-red-500/10"
                onClick={() => onUpdateStatus(alert.id, 'new', notes)} data-testid={`alert-reopen-${alert.id}`}>
                Rouvrir
              </Button>
            )}
          </div>

          <p className="text-[10px] text-muted-foreground">
            Détecté : {new Date(alert.detected_at).toLocaleString('fr-FR')}
            {alert.updated_at !== alert.detected_at && ` · MAJ : ${new Date(alert.updated_at).toLocaleString('fr-FR')}`}
          </p>
        </div>
      )}
    </div>
  );
}
